import { gql } from "@apollo/client";
import { CAR } from "Models/CAR";

export const GET_CARS_BY_AUDIT = gql`
  query getCarsByAudit($whereVariables: cars_bool_exp) {
    cars(where: $whereVariables, order_by: { createdAt: desc }) {
      id
      carReference
      class
      status
      performanceIndicator
      createdAt
      reporter {
        id
        fullName
      }
      agreedVerificationDate
      actualVerificationDate
      carScore
      componentsAffected
      systemAreasAffected
      affectedSince
      initialAgreedVerificationDate
      initialClass
      isDeleted
      operatorActionRequired
      anpgActionRequired
      auditorActionRequired
      fpsoArea: fpso_area {
        fpso {
          block {
            owner {
              companyName
            }
          }
        }
      }
      englishTranslation: car_translations(
        where: { languageCode: { _eq: "en" } }
      ) {
        id
        languageCode
        auditFinding
        description
        furtherDescription
        languageCode
      }
      portugueseTranslation: car_translations(
        where: { languageCode: { _eq: "pt" } }
      ) {
        id
        languageCode
        auditFinding
        description
        furtherDescription
        languageCode
      }
      itemsWithSubSystems: items_with_sub_systems {
        createdAt
        id
        itemId
        subSystemType
        updatedAt
      }
    }
  }
`;

export interface GetCarsByAuditQueryResult {
  cars: CAR[];
}

export interface GetCarsByAuditQueryVariables {
  whereVariables: object;
}

export const INSERT_CAR = gql`
  mutation insertCar(
    $id: uniqueidentifier!
    $class: String
    $carScore: Int
    $initialClass: String
    $descriptionEnglish: String
    $descriptionPortuguese: String
    $reporterId: uniqueidentifier!
    $auditId: uniqueidentifier!
    $fpsoAreaId: uniqueidentifier!
    $createdAt: datetime2
  ) {
    insertCar: insert_cars(
      objects: {
        id: $id
        class: $class
        carScore: $carScore
        initialClass: $class
        performanceIndicator: Grey
        status: OPEN
        reporterId: $reporterId
        auditId: $auditId
        fpsoAreaId: $fpsoAreaId
        createdAt: $createdAt
      }
    ) {
      returning {
        id
      }
    }
    insertEnglishCarTranslation: insert_car_translations(
      objects: {
        languageCode: en
        description: $descriptionEnglish
        carId: $id
      }
    ) {
      returning {
        id
      }
    }
    insertPortugueseCarTranslation: insert_car_translations(
      objects: {
        languageCode: pt
        description: $descriptionPortuguese
        carId: $id
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const UPDATE_CAR = gql`
  mutation updateCar(
    $id: uniqueidentifier!
    $class: String
    $status: String
    $agreedVerificationDate: date
    $actualVerificationDate: date
    $affectedSince: date
    $initialAgreedVerificationDate: date
    $carScore: Int
    $componentsAffected: String
    $systemAreasAffected: String
    $englishTranslationId: uniqueidentifier
    $acceptedEvidenceEnglish: String
    $auditFindingEnglish: String
    $communicationsOnCarEnglish: String
    $descriptionEnglish: String
    $furtherDescriptionEnglish: String
    $portugueseTranslationId: uniqueidentifier
    $acceptedEvidencePortuguese: String
    $auditFindingPortuguese: String
    $communicationsOnCarPortuguese: String
    $descriptionPortuguese: String
    $furtherDescriptionPortuguese: String
    $createdAt: datetime2
  ) {
    update_cars(
      where: { id: { _eq: $id } }
      _set: {
        class: $class
        status: $status
        agreedVerificationDate: $agreedVerificationDate
        actualVerificationDate: $actualVerificationDate
        affectedSince: $affectedSince
        initialAgreedVerificationDate: $initialAgreedVerificationDate
        carScore: $carScore
        componentsAffected: $componentsAffected
        systemAreasAffected: $systemAreasAffected
        createdAt: $createdAt
      }
    ) {
      returning {
        id
      }
    }
    update_english_translation: update_car_translations(
      where: { id: { _eq: $englishTranslationId } }
      _set: {
        auditFinding: $auditFindingEnglish
        acceptedEvidence: $acceptedEvidenceEnglish
        communicationsOnCar: $communicationsOnCarEnglish
        description: $descriptionEnglish
        furtherDescription: $furtherDescriptionEnglish
      }
    ) {
      returning {
        id
      }
    }
    update_portuguese_translation: update_car_translations(
      where: { id: { _eq: $portugueseTranslationId } }
      _set: {
        auditFinding: $auditFindingPortuguese
        acceptedEvidence: $acceptedEvidencePortuguese
        communicationsOnCar: $communicationsOnCarPortuguese
        description: $descriptionPortuguese
        furtherDescription: $furtherDescriptionPortuguese
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const SOFT_DELETE_CAR = gql`
  mutation softDeleteCar($id: uniqueidentifier!) {
    update_cars(where: { id: { _eq: $id } }, _set: { isDeleted: true }) {
      returning {
        id
      }
    }
  }
`;

export const GET_ARCHIVED_CARS_BY_AUDIT_SNAPSHOT = gql`
  query getArchivedCarsByAuditSnapshot(
    $auditSnapshotId: uniqueidentifier
    $whereVariables: car_snapshots_bool_exp
  ) {
    carSnapshots: car_snapshots(
      where: $whereVariables
      order_by: { createdAt: desc }
    ) {
      id
      carReference
      class
      status
      performanceIndicator
      createdAt
      reporter {
        id
        fullName
      }
      agreedVerificationDate
      actualVerificationDate
      carScore
      componentsAffected
      systemAreasAffected
      affectedSince
      initialAgreedVerificationDate
      initialClass
      isDeleted
      carId
      operatorActionRequired
      anpgActionRequired
      auditorActionRequired
      fpsoArea: fpso_area {
        fpso {
          block {
            owner {
              companyName
            }
          }
        }
      }
      englishTranslation: car_translations_snapshots(
        where: { languageCode: { _eq: "en" } }
      ) {
        id
        languageCode
        auditFinding
        description
        furtherDescription
        languageCode
        communicationsOnCar
        acceptedEvidence
      }
      portugueseTranslation: car_translations_snapshots(
        where: { languageCode: { _eq: "pt" } }
      ) {
        id
        languageCode
        auditFinding
        description
        furtherDescription
        languageCode
        communicationsOnCar
        acceptedEvidence
      }
      itemsWithSubSystems: items_with_sub_systems {
        createdAt
        id
        itemId
        subSystemType
        updatedAt
      }
    }
  }
`;

export interface GetArchivedCarsByAuditSnapshotQueryResult {
  carSnapshots: CAR[];
}

export interface GetArchivedCarsByAuditSnapshotQueryVariables {
  auditSnapshotId: string;
  whereVariables: object;
}

export const GET_CAR_EVIDENCE_DOCUMENTS_BY_CAR_ID = gql`
  query getCarEvidenceDocumentsByCarId($id: uniqueidentifier!) {
    car: cars_by_pk(id: $id) {
      id
      carReference
      carEvidenceDocuments: car_evidences(order_by: { updatedAt: desc }) {
        id
        documentDescription
        evidenceDocumentName
        evidenceDocumentReference
        evidenceStatus
        createdAt
        updatedAt
        revision
        documentNumber
        submitter {
          id
          fullName
        }
        comments: car_evidence_discussions(order_by: { createdAt: desc }) {
          id
          content
          createdAt
          updatedAt
          commenter {
            id
            fullName
          }
        }
        itemsWithSubSystems: items_with_sub_systems {
          createdAt
          id
          itemId
          subSystemType
          updatedAt
        }
      }
    }
  }
`;

export interface GetCarEvidenceDocumentsByCarIdQueryResult {
  car: CAR;
}

export interface GetCarEvidenceDocumentsByCarIdQueryVariables {
  id: string;
}

export const INSERT_CAR_EVIDENCE_DOCUMENT = gql`
  mutation insertCarEvidence(
    $evidenceDocumentName: String
    $evidenceDocumentReference: String
    $evidenceStatus: String
    $revision: String
    $documentNumber: String
    $documentDescription: String
    $carId: uniqueidentifier!
    $submitterId: uniqueidentifier!
    $createdAt: datetime2
  ) {
    result: insert_car_evidence(
      objects: {
        evidenceDocumentName: $evidenceDocumentName
        evidenceDocumentReference: $evidenceDocumentReference
        evidenceStatus: $evidenceStatus
        revision: $revision
        documentNumber: $documentNumber
        documentDescription: $documentDescription
        carId: $carId
        submitterId: $submitterId
        createdAt: $createdAt
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const DELETE_CAR_EVIDENCE_DOCUMENT = gql`
  mutation deleteCarEvidenceDocument($id: uniqueidentifier!) {
    result: delete_car_evidence(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;

export const UPDATE_CAR_EVIDENCE_DOCUMENT = gql`
  mutation updateCarEvidenceDocument(
    $id: uniqueidentifier!
    $evidenceDocumentName: String
    $revision: String
    $documentNumber: String
    $documentDescription: String
    $evidenceStatus: String
    $createdAt: datetime2
  ) {
    update_car_evidence(
      where: { id: { _eq: $id } }
      _set: {
        evidenceDocumentName: $evidenceDocumentName
        revision: $revision
        documentNumber: $documentNumber
        documentDescription: $documentDescription
        evidenceStatus: $evidenceStatus
        createdAt: $createdAt
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const INSERT_CAR_EVIDENCE_COMMENT = gql`
  mutation insertCarEvidenceComment(
    $id: uniqueidentifier!
    $carEvidenceId: uniqueidentifier!
    $commenterId: uniqueidentifier!
    $content: String
  ) {
    result: insert_car_evidence_discussion(
      objects: {
        id: $id
        carEvidenceId: $carEvidenceId
        commenterId: $commenterId
        content: $content
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const UPDATE_CAR_EVIDENCE_COMMENT = gql`
  mutation updateCarEvidenceComment($id: uniqueidentifier!, $content: String) {
    update_car_evidence_discussion(
      where: { id: { _eq: $id } }
      _set: { content: $content }
    ) {
      returning {
        id
      }
    }
  }
`;

export const DELETE_CAR_EVIDENCE_COMMENT = gql`
  mutation deleteCarEvidenceComment($id: uniqueidentifier!) {
    result: delete_car_evidence_discussion(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;
